import { API_BASE_URL } from "@lib/config"

type QueryValue = string | number | boolean | null | undefined
type QueryParams = Record<string, QueryValue>

export const STOREFRONT_INTERNAL_PATH_PREFIX = "/_storefront"
export const STOREFRONT_LEGACY_API_PATH_PREFIX = "/api"

const toQueryString = (params?: QueryParams): string => {
  if (!params) return ""

  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (typeof value === "undefined" || value === null) return
    searchParams.set(key, String(value))
  })

  const query = searchParams.toString()
  return query ? `?${query}` : ""
}

const buildRoute = (
  prefix: string,
  path: string,
  params?: QueryParams
): string => `${prefix}${path}${toQueryString(params)}`

const dedupeUrls = (urls: string[]): string[] => {
  const seen = new Set<string>()
  const unique: string[] = []
  urls.forEach((url) => {
    const normalized = String(url || "").trim()
    if (!normalized || seen.has(normalized)) return
    seen.add(normalized)
    unique.push(normalized)
  })
  return unique
}

const isLikelyLegacyProxyMiss = (response: Response): boolean => {
  if (response.status !== 404) return false
  const contentType = response.headers.get("content-type") || ""
  return /text\/html/i.test(contentType)
}

export const buildCanonicalStorefrontRoute = (
  path: string,
  params?: QueryParams
): string => buildRoute(STOREFRONT_INTERNAL_PATH_PREFIX, path, params)

export const buildLegacyStorefrontRoute = (
  path: string,
  params?: QueryParams
): string => buildRoute(STOREFRONT_LEGACY_API_PATH_PREFIX, path, params)

export const buildCheckoutSessionRouteCandidates = (options?: {
  cartId?: string | null
}): string[] =>
  dedupeUrls([
    buildCanonicalStorefrontRoute("/checkout/session", {
      cartId: options?.cartId || undefined,
    }),
    buildLegacyStorefrontRoute("/checkout/session", {
      cartId: options?.cartId || undefined,
    }),
  ])

export const buildCheckoutSessionResumeRouteCandidates = (
  token: string
): string[] =>
  dedupeUrls([
    buildCanonicalStorefrontRoute(
      `/checkout/session/resume/${encodeURIComponent(token)}`
    ),
    buildLegacyStorefrontRoute(
      `/checkout/session/resume/${encodeURIComponent(token)}`
    ),
  ])

export const buildDlocalCreatePaymentRouteCandidates = (): string[] =>
  dedupeUrls([
    buildCanonicalStorefrontRoute("/dlocal/create-payment"),
    buildLegacyStorefrontRoute("/dlocal/create-payment"),
  ])

export const buildOrderStatusRouteCandidates = (
  orderId: string,
  options?: { token?: string | null }
): string[] =>
  dedupeUrls([
    buildCanonicalStorefrontRoute(`/orders/${encodeURIComponent(orderId)}`, {
      token: options?.token || undefined,
    }),
    buildLegacyStorefrontRoute(`/orders/${encodeURIComponent(orderId)}`, {
      token: options?.token || undefined,
    }),
  ])

export const buildCountryRouteCandidates = (): string[] =>
  dedupeUrls([
    buildCanonicalStorefrontRoute("/country"),
    buildLegacyStorefrontRoute("/country"),
    `${API_BASE_URL}/public/storefront/country`,
  ])

export const buildCurrencyRouteCandidates = (): string[] =>
  dedupeUrls([
    buildCanonicalStorefrontRoute("/currency"),
    buildLegacyStorefrontRoute("/currency"),
  ])

export const buildForexLatestRouteCandidates = (): string[] =>
  dedupeUrls([
    buildCanonicalStorefrontRoute("/forex/latest"),
    buildLegacyStorefrontRoute("/forex/latest"),
    `${API_BASE_URL}/public/forex/latest`,
  ])

export const fetchFirstReachableStorefrontResponse = async (
  urls: string[],
  init: RequestInit
): Promise<Response | null> => {
  const candidates = dedupeUrls(urls)

  for (let index = 0; index < candidates.length; index += 1) {
    const url = candidates[index]
    const hasMoreCandidates = index < candidates.length - 1

    try {
      const response = await fetch(url, init)
      if (hasMoreCandidates && isLikelyLegacyProxyMiss(response)) {
        continue
      }
      return response
    } catch {
      if (!hasMoreCandidates) {
        return null
      }
    }
  }

  return null
}
